import { useLanguage } from '../i18n/LanguageContext';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { createAdmin, deleteAdmin, fetchAdmins } from '../api/admins';
import { createDefaultAdminPermissions, fetchAdminPermissions, saveAdminPermissions } from '../api/adminPermissions';
import { getCurrentUser } from '../utils/auth';
import { hashPassword } from '../utils/crypto';
import { DEFAULT_MANAGER_PERMISSIONS, can, setAdminPermissionCache } from '../utils/permissions';
import './SettingsPage.css';

const ADMIN_ROLE_OPTIONS = ['Admin', 'Manager'];
const DEFAULT_ADMIN_CREATE_ROLE = 'Admin';
const ADMIN_ROLE_DESCRIPTIONS = {
  Admin: '상위 관리자 권한입니다. 설정과 관리자 관리 기능 접근을 전제로 합니다.',
  Manager: 'CS 운영 관리자 권한입니다. 사용자 관리와 주요 운영 업무를 수행합니다.',
};
const PERMISSION_GROUPS = [
  {
    title: '사용자 관리',
    items: [
      { key: 'user.read', label: '사용자 조회' },
      { key: 'user.email.update', label: '이메일 변경' },
      { key: 'user.password.reset', label: '비밀번호 재설정 메일' },
      { key: 'user.delete', label: '사용자 삭제' },
    ],
  },
  {
    title: '기기 관리',
    items: [
      { key: 'device.read', label: '기기 조회' },
    ],
  },
  {
    title: '쿠폰 관리',
    items: [
      { key: 'coupon.read', label: '쿠폰 조회' },
      { key: 'coupon.create', label: '쿠폰 생성' },
      { key: 'coupon.bulk.create', label: '쿠폰 대량 생성' },
      { key: 'coupon.register', label: '사용자 쿠폰 등록' },
    ],
  },
  {
    title: '크레딧 관리',
    items: [
      { key: 'credit.read', label: '크레딧 조회' },
      { key: 'credit.claim', label: '크레딧 지급·회수' },
    ],
  },
  {
    title: '메일 관리',
    items: [
      { key: 'mail.read', label: '메일 조회' },
      { key: 'mail.resend', label: '인증 메일 재발송' },
    ],
  },
  {
    title: '설정 관리',
    items: [
      { key: 'admin.read', label: '관리자 조회' },
      { key: 'admin.create', label: '관리자 추가' },
      { key: 'admin.delete', label: '관리자 삭제' },
      { key: 'settings.manage', label: '권한 설정 변경' },
    ],
  },
];
const PERMISSION_ITEMS = PERMISSION_GROUPS.flatMap((group) => group.items);

function formatDate(dateString) {
  if (!dateString) return '-';
  const d = new Date(dateString);
  return `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateTime(dateString) {
  if (!dateString) return '-';
  const d = new Date(dateString);
  return `${formatDate(dateString)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function getDefaultPermissions(admin) {
  return PERMISSION_ITEMS.reduce((acc, permission) => {
    acc[permission.key] = admin?.role === 'Admin'
      ? true
      : DEFAULT_MANAGER_PERMISSIONS[permission.key] === true;
    return acc;
  }, {});
}

function sortAdminsByCreatedAt(admins) {
  return [...admins].sort((a, b) => {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  });
}

function getErrorMessage(error, fallback) {
  const message = error?.response?.data?.message;
  const normalizedMessage = Array.isArray(message) ? message.join(', ') : message;

  switch (normalizedMessage) {
    case 'Username or email already exists':
      return '이미 사용 중인 아이디 또는 이메일입니다.';
    case 'Admin role required':
      return '관리자 권한이 필요합니다.';
    case 'Password is required':
      return '비밀번호를 입력해주세요.';
    case 'Email is required':
      return '이메일을 입력해주세요.';
    case 'Username is required':
      return '아이디를 입력해주세요.';
    default:
      return normalizedMessage || fallback;
  }
}

function SettingsPage() {
  const { t } = useLanguage();
  const canCreateAdmin = can('admin.create');
  const canDeleteAdmin = can('admin.delete');
  const canManageSettings = can('settings.manage');
  const [admins, setAdmins] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingUuid, setDeletingUuid] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [createConfirmOpen, setCreateConfirmOpen] = useState(false);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [permissionEditing, setPermissionEditing] = useState(false);
  const [permissionDraft, setPermissionDraft] = useState({});
  const [adminPermissions, setAdminPermissions] = useState({});
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [permissionSaving, setPermissionSaving] = useState(false);
  const [permissionError, setPermissionError] = useState('');
  const currentUser = useMemo(() => getCurrentUser(), []);
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    role: DEFAULT_ADMIN_CREATE_ROLE,
  });

  const loadAdmins = async () => {
    setLoading(true);
    setListError('');

    try {
      const result = await fetchAdmins({ offset: 0, limit: 50 });
      setAdmins(result.data.users ?? []);
      setTotal(result.data.total ?? 0);
    } catch (err) {
      setAdmins([]);
      setTotal(0);
      setListError(getErrorMessage(err, '관리자 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const adminStats = useMemo(() => {
    return {
      total,
      admin: admins.filter((admin) => admin.role === 'Admin').length,
      staff: admins.filter((admin) => admin.role !== 'Admin').length,
    };
  }, [admins, total]);

  const filteredAdmins = useMemo(() => {
    if (roleFilter === 'admin') {
      return admins.filter((admin) => admin.role === 'Admin');
    }

    if (roleFilter === 'operator') {
      return admins.filter((admin) => admin.role !== 'Admin');
    }

    return admins;
  }, [admins, roleFilter]);

  const groupedAdmins = useMemo(() => {
    const adminRows = sortAdminsByCreatedAt(
      filteredAdmins.filter((admin) => admin.role === 'Admin'),
    );
    const managerRows = sortAdminsByCreatedAt(
      filteredAdmins.filter((admin) => admin.role !== 'Admin'),
    );
    const groups = [];

    if (roleFilter !== 'operator' && adminRows.length > 0) {
      groups.push({
        key: 'admin',
        title: 'Admin',
        description: '최고 관리자',
        rows: adminRows,
      });
    }

    if (roleFilter !== 'admin' && managerRows.length > 0) {
      groups.push({
        key: 'manager',
        title: 'Manager',
        description: '운영 관리자',
        rows: managerRows,
      });
    }

    return groups;
  }, [filteredAdmins, roleFilter]);

  const selectedAdminPermissions = useMemo(() => {
    if (!selectedAdmin) return {};
    return {
      ...getDefaultPermissions(selectedAdmin),
      ...(adminPermissions[selectedAdmin.uuid] || {}),
    };
  }, [adminPermissions, selectedAdmin]);

  const permissionsToMap = (admin, permissions = []) => {
    const nextPermissions = Array.isArray(permissions)
      ? permissions.reduce((acc, permission) => {
        if (permission?.permissionKey) {
          acc[permission.permissionKey] = permission.enabled === true;
        }
        return acc;
      }, {})
      : permissions;

    return {
      ...getDefaultPermissions(admin),
      ...(nextPermissions || {}),
    };
  };

  const openAdminDetail = async (admin) => {
    const defaultPermissions = getDefaultPermissions(admin);

    setSelectedAdmin(admin);
    setPermissionDraft(defaultPermissions);
    setPermissionEditing(false);
    setPermissionError('');

    if (admin.role === 'Admin') {
      setAdminPermissions((prev) => ({
        ...prev,
        [admin.uuid]: defaultPermissions,
      }));
      return;
    }

    setPermissionLoading(true);

    try {
      const permissions = await fetchAdminPermissions(admin.uuid);
      const permissionMap = permissionsToMap(admin, permissions);

      setAdminPermissions((prev) => ({
        ...prev,
        [admin.uuid]: permissionMap,
      }));
      setPermissionDraft(permissionMap);
      setAdminPermissionCache(admin.uuid, permissionMap);
    } catch (err) {
      console.error('Failed to fetch admin permissions:', err);
      setAdminPermissions((prev) => ({
        ...prev,
        [admin.uuid]: defaultPermissions,
      }));
      setPermissionError('권한 설정을 불러오지 못했습니다. 기본 권한으로 표시합니다.');
    } finally {
      setPermissionLoading(false);
    }
  };

  const closeAdminDetail = () => {
    setSelectedAdmin(null);
    setPermissionEditing(false);
    setPermissionDraft({});
    setPermissionError('');
    setPermissionLoading(false);
    setPermissionSaving(false);
  };

  const startPermissionEditing = () => {
    setPermissionDraft(selectedAdminPermissions);
    setPermissionEditing(true);
  };

  const cancelPermissionEditing = () => {
    setPermissionDraft(selectedAdminPermissions);
    setPermissionEditing(false);
  };

  const togglePermissionDraft = (permissionKey) => {
    setPermissionDraft((prev) => ({
      ...prev,
      [permissionKey]: !prev[permissionKey],
    }));
  };

  const savePermissionDraft = async () => {
    if (!selectedAdmin) return;

    setPermissionSaving(true);
    setPermissionError('');

    try {
      const permissions = PERMISSION_ITEMS.map((permission) => ({
        permissionKey: permission.key,
        enabled: permissionDraft[permission.key] === true,
      }));
      const savedPermissions = await saveAdminPermissions(selectedAdmin.uuid, permissions);
      const permissionMap = permissionsToMap(selectedAdmin, savedPermissions);

      setAdminPermissions((prev) => ({
        ...prev,
        [selectedAdmin.uuid]: permissionMap,
      }));
      setPermissionDraft(permissionMap);
      setAdminPermissionCache(selectedAdmin.uuid, permissionMap);
      setPermissionEditing(false);
    } catch (err) {
      console.error('Failed to save admin permissions:', err);
      setPermissionError('권한 설정 저장에 실패했습니다.');
    } finally {
      setPermissionSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreateAdmin) {
      setFormError('관리자 추가 권한이 없습니다.');
      return;
    }

    const username = form.username.trim();
    const email = form.email.trim().toLowerCase();
    const password = form.password;

    if (!username || !email || !password) {
      setFormError('아이디, 이메일, 비밀번호를 모두 입력해주세요.');
      return;
    }

    if (password.length < 8) {
      setFormError('비밀번호는 8자 이상 입력해주세요.');
      return;
    }

    setFormError('');
    setCreateConfirmOpen(true);
  };

  const closeCreateConfirmModal = () => {
    if (submitting) return;
    setCreateConfirmOpen(false);
  };

  const handleCreateConfirm = async () => {
    if (!canCreateAdmin) {
      setFormError('관리자 추가 권한이 없습니다.');
      setCreateConfirmOpen(false);
      return;
    }

    const username = form.username.trim();
    const email = form.email.trim().toLowerCase();
    const password = form.password;
    const role = form.role;

    setSubmitting(true);
    setFormError('');

    try {
      const hashedPassword = await hashPassword(password);
      const createdAdmin = await createAdmin({
        username,
        email,
        password: hashedPassword,
        role,
      });

      if (createdAdmin?.uuid && role === 'Manager') {
        await createDefaultAdminPermissions(createdAdmin.uuid);
      }

      await loadAdmins();

      setForm({
        username: '',
        email: '',
        password: '',
        role: DEFAULT_ADMIN_CREATE_ROLE,
      });
      setCreateConfirmOpen(false);
    } catch (err) {
      setFormError(getErrorMessage(err, '관리자 추가에 실패했습니다.'));
      setCreateConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteModal = (admin) => {
    if (!canDeleteAdmin) return;
    setDeleteTarget(admin);
    setDeleteConfirmText('');
    setListError('');
  };

  const closeDeleteModal = () => {
    if (deletingUuid) return;
    setDeleteTarget(null);
    setDeleteConfirmText('');
  };

  const handleDelete = async () => {
    if (!canDeleteAdmin) return;
    if (!deleteTarget || deleteConfirmText !== '관리자 삭제') return;

    setDeletingUuid(deleteTarget.uuid);
    setListError('');

    try {
      await deleteAdmin(deleteTarget.uuid);
      setAdmins((prev) => prev.filter((admin) => admin.uuid !== deleteTarget.uuid));
      setTotal((prev) => Math.max(prev - 1, 0));
      closeDeleteModal();
    } catch (err) {
      setListError(getErrorMessage(err, '관리자 삭제에 실패했습니다.'));
    } finally {
      setDeletingUuid(null);
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h2 className="page-title">{t("설정")}</h2>
          <p className="page-subtitle">{t("관리자 계정을 확인하고 운영 권한을 관리할 수 있습니다.")}</p>
        </div>
      </div>

      <div className="settings-summary-grid">
        <button
          className={`summary-panel ${roleFilter === 'all' ? 'active' : ''}`}
          type="button"
          onClick={() => setRoleFilter('all')}
        >
          <span>{t("전체 관리자")}</span>
          <strong>{adminStats.total}</strong>
        </button>
        <button
          className={`summary-panel ${roleFilter === 'admin' ? 'active' : ''}`}
          type="button"
          onClick={() => setRoleFilter('admin')}
        >
          <span>{t("최고 관리자")}</span>
          <strong>{adminStats.admin}</strong>
        </button>
        <button
          className={`summary-panel ${roleFilter === 'operator' ? 'active' : ''}`}
          type="button"
          onClick={() => setRoleFilter('operator')}
        >
          <span>{t("운영 관리자")}</span>
          <strong>{adminStats.staff}</strong>
        </button>
      </div>

      <div className="settings-content">
        <section className="admin-list-section">
          <div className="section-header">
            <h3>{t("관리자 목록")}</h3>
          </div>

          {listError && <div className="settings-error">{listError}</div>}

          <div className="table-card">
            <table className="settings-table">
              <thead>
                <tr>
                  <th>USERNAME</th>
                  <th>EMAIL</th>
                  <th>{t("권한")}</th>
                  <th>{t("등록일")}</th>
                  <th>{t("최근 로그인")}</th>
                  <th className="col-action">{t("관리")}</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="6" className="empty-cell">
                      {t("관리자 목록을 불러오는 중입니다.")}
                    </td>
                  </tr>
                )}

                {!loading && groupedAdmins.length === 0 && (
                  <tr>
                    <td colSpan="6" className="empty-cell">
                      {t("표시할 관리자가 없습니다.")}
                    </td>
                  </tr>
                )}

                {!loading && groupedAdmins.map((group) => (
                  <Fragment key={group.key}>
                    <tr className="admin-group-row">
                      <td colSpan="6">
                        <span>{group.title}</span>
                        <small>{group.description} · {group.rows.length.toLocaleString()}명</small>
                      </td>
                    </tr>
                    {group.rows.map((admin) => (
                      <tr
                        key={admin.uuid}
                        className="admin-row"
                        onClick={() => openAdminDetail(admin)}
                      >
                        <td className="username-cell">{admin.username}</td>
                        <td>{admin.email}</td>
                        <td>
                          <span className={`role-badge ${admin.role.toLowerCase()}`}>
                            {admin.role}
                          </span>
                        </td>
                        <td>{formatDate(admin.createdAt)}</td>
                        <td>{formatDate(admin.lastSignIn)}</td>
                        <td className="col-action">
                          {admin.uuid === currentUser?.uuid ? (
                            <button className="delete-btn" type="button" disabled title="현재 로그인한 계정은 삭제할 수 없습니다.">
                              {t("본인")}
                            </button>
                          ) : (
                          <button
                            className="delete-btn"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDeleteModal(admin);
                            }}
                            disabled={deletingUuid === admin.uuid || !canDeleteAdmin}
                            title={!canDeleteAdmin ? '관리자 삭제 권한이 없습니다.' : undefined}
                          >
                            {deletingUuid === admin.uuid ? '삭제 중' : '삭제'}
                          </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="admin-form-section">
          <div className="section-header">
            <h3>{t("관리자 추가")}</h3>
          </div>

          <form className="admin-form" onSubmit={handleSubmit}>
            <label>
              <span>{t("아이디")}</span>
              <input
                type="text"
                name="username"
                value={form.username}
                onChange={handleChange}
                placeholder="admin_id"
                disabled={!canCreateAdmin}
              />
            </label>

            <label>
              <span>{t("이메일")}</span>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="admin@squaregolf.com"
                disabled={!canCreateAdmin}
              />
            </label>

            <label>
              <span>{t("초기 비밀번호")}</span>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder={t("8자 이상 입력")}
                disabled={!canCreateAdmin}
              />
              <small className="password-help">{t("8자 이상 입력해주세요. 생성 후 등록된 이메일로 인증 메일이 발송됩니다.")}</small>
            </label>

            <label>
              <span className="admin-role-label">
                {t("권한")}
                <span className="admin-role-help">
                  <button type="button" className="admin-role-help-trigger" aria-label={t("권한 설명")}>
                    ?
                  </button>
                  <span className="admin-role-help-popover" role="tooltip">
                    <strong>{t("권한 안내")}</strong>
                    {ADMIN_ROLE_OPTIONS.map((role) => (
                      <span className="admin-role-help-row" key={role}>
                        <span className="admin-role-help-name">{role}</span>
                        <span>{ADMIN_ROLE_DESCRIPTIONS[role]}</span>
                      </span>
                    ))}
                  </span>
                </span>
              </span>
              <select name="role" value={form.role} onChange={handleChange} disabled={!canCreateAdmin}>
                {ADMIN_ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </label>

            <button className="submit-btn" type="submit" disabled={submitting || !canCreateAdmin}>
              {submitting ? t("추가 중") : t("관리자 추가")}
            </button>
            {!canCreateAdmin && <div className="form-error">관리자 추가 권한이 없습니다.</div>}

            {formError && <div className="form-error">{formError}</div>}
          </form>
        </section>
      </div>

      {selectedAdmin && (
        <>
          <div className="settings-drawer-overlay" onClick={closeAdminDetail}></div>
          <aside className="admin-detail-drawer" aria-label={t("관리자 상세 정보")}>
            <div className="admin-detail-header">
              <div>
                <p>Admin Detail</p>
                <h3>{t("관리자 상세")}</h3>
              </div>
              <button className="settings-modal-close" type="button" onClick={closeAdminDetail} aria-label={t("닫기")}>
                ×
              </button>
            </div>

            <div className="admin-detail-body">
              <div className="admin-detail-layout">
              <section className="admin-detail-section">
                <div className="admin-detail-section-header">
                  <div>
                    <h4>{t("기능 권한")}</h4>
                    <p>{t("권한 설정은 서버 DB에 저장됩니다.")}</p>
                  </div>
                  {selectedAdmin.role === 'Admin' ? (
                    <span className="admin-permission-fixed">{t("전체 허용")}</span>
                  ) : permissionEditing ? (
                    <div className="permission-edit-actions">
                      <button type="button" className="permission-cancel-btn" onClick={cancelPermissionEditing}>
                        {t("취소")}
                      </button>
                      <button
                        type="button"
                        className="permission-save-btn"
                        onClick={savePermissionDraft}
                        disabled={permissionSaving}
                      >
                        {permissionSaving ? '저장 중' : t("저장")}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="permission-edit-btn"
                      onClick={startPermissionEditing}
                      disabled={!canManageSettings || currentUser?.role !== 'Admin' || permissionLoading}
                      title={!canManageSettings ? '권한 설정 변경 권한이 없습니다.' : undefined}
                    >
                      {t("권한 수정")}
                    </button>
                  )}
                </div>

                {permissionError && <div className="settings-error">{permissionError}</div>}

                <div className="permission-group-list">
                  {PERMISSION_GROUPS.map((group) => (
                    <div className="permission-group" key={group.title}>
                      <h5>{group.title}</h5>
                      {group.items.map((permission) => {
                        const enabled = permissionEditing
                          ? permissionDraft[permission.key]
                          : selectedAdminPermissions[permission.key];

                        return (
                          <label className="permission-toggle-row" key={permission.key}>
                            <span>
                              <strong>{t(permission.label)}</strong>
                              <small>{permission.key}</small>
                            </span>
                            <input
                              type="checkbox"
                              checked={Boolean(enabled)}
                              disabled={!permissionEditing || selectedAdmin.role === 'Admin' || permissionLoading || permissionSaving}
                              onChange={() => togglePermissionDraft(permission.key)}
                            />
                            <span className="permission-switch" aria-hidden="true"></span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </section>
              <div className="admin-detail-main-column">
                <section className="admin-detail-profile">
                  <div>
                    <span className="admin-detail-label">USERNAME</span>
                    <strong>{selectedAdmin.username}</strong>
                  </div>
                  <span className={`role-badge ${selectedAdmin.role.toLowerCase()}`}>
                    {selectedAdmin.role}
                  </span>
                </section>

                <section className="admin-detail-section">
                  <div className="admin-detail-section-header">
                    <h4>{t("기본 정보")}</h4>
                  </div>
                  <div className="admin-info-grid">
                    <div className="admin-info-item">
                      <span>UUID</span>
                      <strong className="admin-info-mono">{selectedAdmin.uuid}</strong>
                    </div>
                    <div className="admin-info-item">
                      <span>{t("이메일")}</span>
                      <strong>{selectedAdmin.email || '-'}</strong>
                    </div>
                    <div className="admin-info-item">
                      <span>{t("인증 상태")}</span>
                      <strong>{selectedAdmin.verified ? t("인증 완료") : t("미인증")}</strong>
                    </div>
                    <div className="admin-info-item">
                      <span>{t("가입 IP")}</span>
                      <strong>{selectedAdmin.joinIp || '-'}</strong>
                    </div>
                    <div className="admin-info-item">
                      <span>{t("등록일")}</span>
                      <strong>{formatDateTime(selectedAdmin.createdAt)}</strong>
                    </div>
                    <div className="admin-info-item">
                      <span>{t("수정일")}</span>
                      <strong>{formatDateTime(selectedAdmin.updatedAt)}</strong>
                    </div>
                    <div className="admin-info-item">
                      <span>{t("최근 로그인")}</span>
                      <strong>{formatDateTime(selectedAdmin.lastSignIn)}</strong>
                    </div>
                    <div className="admin-info-item">
                      <span>{t("최근 로그아웃")}</span>
                      <strong>{formatDateTime(selectedAdmin.lastSignOut)}</strong>
                    </div>
                  </div>
                </section>
              </div>
              </div>
            </div>
          </aside>
        </>
      )}

      {createConfirmOpen && (
        <div className="settings-modal-backdrop" role="presentation">
          <div className="settings-modal" role="dialog" aria-modal="true" aria-labelledby="admin-create-title">
            <div className="settings-modal-header">
              <h3 id="admin-create-title">{t("관리자 추가 확인")}</h3>
              <button
                className="settings-modal-close"
                type="button"
                onClick={closeCreateConfirmModal}
                aria-label={t("닫기")}
              >
                ×
              </button>
            </div>

            <div className="settings-modal-body">
              <div className="delete-target-row">
                <span>{t("아이디")}</span>
                <strong>{form.username.trim()}</strong>
              </div>
              <div className="delete-target-row">
                <span>{t("이메일")}</span>
                <strong>{form.email.trim().toLowerCase()}</strong>
              </div>
              <div className="delete-target-row">
                <span>{t("권한")}</span>
                <strong>{form.role}</strong>
              </div>
              <div className="create-notice">
                {t("입력한 정보로 계정을 생성하시겠습니까?")}
                <br />
                {t("생성 후 등록된 이메일로 인증 메일이 발송됩니다.")}
              </div>
            </div>

            <div className="settings-modal-actions">
              <button className="cancel-modal-btn" type="button" onClick={closeCreateConfirmModal}>
                {t("취소")}
              </button>
              <button
                className="confirm-create-btn"
                type="button"
                onClick={handleCreateConfirm}
                disabled={submitting}
                title={!canCreateAdmin ? '관리자 추가 권한이 없습니다.' : undefined}
              >
                {submitting ? t("추가 중") : t("추가")}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="settings-modal-backdrop" role="presentation">
          <div className="settings-modal" role="dialog" aria-modal="true" aria-labelledby="admin-delete-title">
            <div className="settings-modal-header">
              <h3 id="admin-delete-title">관리자 삭제 확인</h3>
              <button
                className="settings-modal-close"
                type="button"
                onClick={closeDeleteModal}
                aria-label="닫기"
              >
                ×
              </button>
            </div>

            <div className="settings-modal-body">
              <div className="delete-target-row">
                <span>대상 관리자</span>
                <strong>{deleteTarget.username}</strong>
              </div>
              <div className="delete-target-row">
                <span>이메일</span>
                <strong>{deleteTarget.email}</strong>
              </div>

              <div className="delete-warning">
                관리자 계정을 삭제하면 설정 페이지에서 더 이상 표시되지 않습니다.
                <br />
                계속하려면 아래 입력칸에 관리자 삭제를 입력해주세요.
              </div>

              <label className="delete-confirm-label">
                <span>확인 문구</span>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="관리자 삭제"
                  autoFocus
                />
              </label>
            </div>

            <div className="settings-modal-actions">
              <button className="cancel-modal-btn" type="button" onClick={closeDeleteModal}>
                취소
              </button>
              <button
                className="confirm-delete-btn"
                type="button"
                onClick={handleDelete}
                disabled={deleteConfirmText !== '관리자 삭제' || deletingUuid === deleteTarget.uuid || !canDeleteAdmin}
              >
                {deletingUuid === deleteTarget.uuid ? '삭제 중' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
